import { NgClass, NgIf } from '@angular/common';
import { Component, computed, EventEmitter, input, InputSignal, Output, Signal } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InlineUserChipComponent } from '../../../../../shared/components/inline-user-chip/inline-user-chip.component';
import {
  IStepChoiceDialogData,
  StepChoiceDialogComponent,
} from '../../../../../shared/components/step-choice-dialog/step-choice-dialog.component';
import { TextAvatarComponent } from '../../../../../shared/components/text-avatar/text-avatar.component';
import { ELoadStatus } from '../../../../../state/state.interface';
import { ESnackbar, SnackbarService } from '../../../../../utilities/snackbar.service';
import { EWorkflow, EWorkflowIcon, IWorkflowStepBase } from '../../../items.interface';
import { ItemsService } from '../../../items.service';
import { IKamishibaiCard } from '../items-kamishibai.interface';
import { IKamishibaiCardStore } from './kamishibai-card.store';

const STATUS_ICON: Record<EWorkflow, EWorkflowIcon> = {
  [EWorkflow.todo]: EWorkflowIcon.adjust,
  [EWorkflow.inProgress]: EWorkflowIcon.schedule,
  [EWorkflow.done]: EWorkflowIcon.task_alt,
};

@Component({
  imports: [
    TextAvatarComponent,
    MatIcon,
    MatIconButton,
    NgIf,
    TranslatePipe,
    MatTooltip,
    NgClass,
    InlineUserChipComponent,
  ],
  providers: [IKamishibaiCardStore],
  selector: 'app-kamishibai-card',
  styleUrl: './kamishibai-card.component.scss',
  templateUrl: './kamishibai-card.component.html',
})
export class KamishibaiCardComponent {
  public item: InputSignal<IKamishibaiCard> = input.required<IKamishibaiCard>();
  public isSimpleMode: InputSignal<boolean> = input.required<boolean>();

  public readonly statusIcon: Signal<string> = computed(() => STATUS_ICON[this.item().status] ?? EWorkflowIcon.adjust);

  @Output() public refreshDashboard: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    public readonly store: IKamishibaiCardStore,
    private readonly translate: TranslateService,
    private readonly itemsService: ItemsService,
    private readonly snackbar: SnackbarService,
    private readonly dialog: MatDialog,
  ) {
    this.store.dataStatus$.pipe().subscribe((data: ELoadStatus) => {
      if (data === ELoadStatus.success || data === ELoadStatus.failure) {
        this.refreshDashboard.emit();
      }
    });
  }

  public onStatusChange(event: Event): void {
    event.stopPropagation();

    const statusCategoryProcess: {
      [key in EWorkflow]: EWorkflow;
    } = {
      [EWorkflow.todo]: EWorkflow.inProgress,
      [EWorkflow.inProgress]: EWorkflow.done,
      [EWorkflow.done]: EWorkflow.todo,
    };

    const targetCategory: EWorkflow = statusCategoryProcess[this.item().status];
    const nextSteps: IWorkflowStepBase[] = this.item().nextSteps;
    // Prefer the next status category; when the workflow has no such transition
    // (e.g. To Do → Approval only), fall back to whatever it allows from here.
    const preferredSteps: IWorkflowStepBase[] = nextSteps.filter(
      (step: IWorkflowStepBase) => step.statusCategory === targetCategory,
    );
    const candidateSteps: IWorkflowStepBase[] = preferredSteps.length ? preferredSteps : nextSteps;

    if (!candidateSteps.length) {
      this.snackbar.open(this.translate.instant('page.items.kamishibai.noNextStatus'), ESnackbar.error);

      return;
    }

    if (candidateSteps.length === 1) {
      this.changeStatus(candidateSteps[0]);

      return;
    }

    const dialogData: IStepChoiceDialogData = {
      itemKey: this.item().itemKey,
      itemTitle: this.item().title,
      steps: candidateSteps,
    };

    this.dialog
      .open(StepChoiceDialogComponent, { data: dialogData, restoreFocus: false, width: '400px' })
      .afterClosed()
      .subscribe((stepId: number | undefined) => {
        const chosenStep: IWorkflowStepBase | undefined = candidateSteps.find(
          (step: IWorkflowStepBase) => step.id === stepId,
        );

        if (chosenStep) {
          this.changeStatus(chosenStep);
        }
      });
  }

  private changeStatus(targetStep: IWorkflowStepBase): void {
    this.store.changeItemStatusColor({
      assignment: {
        itemId: this.item().id,
        nextItemId: null,
        previousItemId: null,
        targetWorkflowStepId: targetStep.id,
      },
      successMessage: this.translate.instant('page.items.statusUpdated', {
        key: this.item().itemKey,
        status: targetStep.isDefault ? this.translate.instant(`system.label.${targetStep.name}`) : targetStep.name,
      }),
    });
  }

  public openItemEditModal(itemId: number, itemKey: string, boardKey: string): void {
    this.itemsService.openItemEditDialogAndSetUrl(
      boardKey,
      itemId,
      itemKey,
      this.translate.instant('button.saveChanges'),
    );
  }

  public triggerRefresh(): void {
    this.refreshDashboard.emit();
  }
}
